// The Face Sheet (Part A1) — admission particulars, attenders/next of kin,
// the Medico-Legal Case flag, and payer/scheme.
// Phase 1 of docs/HMS_V4_IP_CASE_SHEET_INDIA_DESIGN.md (§5.1/§6.1-6.4).
//
// Unlike the other sections (which already have an aggregated read inside
// the case-sheet snapshot), this one owns its own four small reads — each
// is its own table with no amendment chain, so a plain query+mutation pair
// per concern is simpler than forcing them through one combined endpoint.
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input, NativeSelect } from '@/components/ui/Input';
import { LoadingState } from '@/components/ui/States';
import { TextPromptDialog } from '@/components/ui/TextPromptDialog';
import { ApiError } from '@/lib/api/client';
import { fetchDoctors } from '@/features/appointments/api';
import { getSpecialties } from '@/features/admin/api';
import {
  useParticulars, useUpdateParticulars,
  useAttenders, useCreateAttender, useUpdateAttender, useDeleteAttender,
  useMlc, useSaveMlc,
  useLegalHold, useSetLegalHold, useClearLegalHold,
  usePayerSchemes, usePayers, useCreatePayer, useUpdatePayer, useDeletePayer, usePayersMaster, useVerifyPayerId,
} from '../hooks';
import type { AdmissionType, AdmissionSource, IdProofType } from '../types';
import { Section, Empty, useCan, fmtDateTime } from '../ui';

function useErr() {
  const [err, setErr] = useState<string | null>(null);
  const fail = (e: unknown) => setErr(e instanceof ApiError ? e.message : 'Something went wrong.');
  const node = err ? <div className="mb-2 rounded bg-danger-soft px-3 py-2 text-[12px] text-danger-ink">{err}</div> : null;
  return { fail, node, clear: () => setErr(null) };
}

const ADMISSION_TYPES: AdmissionType[] = ['emergency', 'elective', 'transfer-in', 'day-care'];
const ADMISSION_SOURCES: AdmissionSource[] = ['opd', 'ed', 'referral', 'direct', 'other'];
const ID_PROOF_TYPES: IdProofType[] = ['aadhaar', 'voter', 'dl', 'passport', 'other'];

export function FaceSheetSection({ admissionId, readOnly }: { admissionId: number; readOnly: boolean }) {
  const can = useCan();
  const mayWrite = can('ip:admission-record') && !readOnly;

  return (
    <div className="space-y-4">
      <ParticularsCard admissionId={admissionId} mayWrite={mayWrite} />
      <AttendersCard admissionId={admissionId} mayWrite={mayWrite} />
      <MlcCard admissionId={admissionId} mayWrite={mayWrite} />
      <LegalHoldCard admissionId={admissionId} mayWrite={can('admin:legal-hold') && !readOnly} />
      <PayerCard admissionId={admissionId} mayWrite={mayWrite} />
    </div>
  );
}

// ── Admission particulars ─────────────────────────────────────────

function ParticularsCard({ admissionId, mayWrite }: { admissionId: number; mayWrite: boolean }) {
  const { data, isLoading } = useParticulars(admissionId);
  const update = useUpdateParticulars(admissionId);
  const { fail, node, clear } = useErr();
  const [doctors, setDoctors] = useState<{ id: number; full_name: string }[]>([]);
  const [specialties, setSpecialties] = useState<{ id: number; name: string }[]>([]);

  useEffect(() => { fetchDoctors().then(setDoctors).catch(() => {}); }, []);
  useEffect(() => { getSpecialties().then(setSpecialties).catch(() => {}); }, []);

  if (isLoading || !data) return <Section title="Admission"><LoadingState label="Loading admission particulars…" /></Section>;

  const set = (patch: Record<string, unknown>) => {
    clear();
    update.mutate(patch as never, { onError: fail });
  };

  return (
    <Section title="Admission" subtitle={data.ip_number ? `IP No. ${data.ip_number}` : 'IP number not yet allocated'}>
      {node}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <label className="mb-1 block text-xs font-semibold">Type</label>
          <NativeSelect value={data.admission_type ?? ''} disabled={!mayWrite}
            onChange={(e) => set({ admission_type: e.target.value || undefined })}>
            <option value="">— not set —</option>
            {ADMISSION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </NativeSelect>
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold">Source</label>
          <NativeSelect value={data.admission_source ?? ''} disabled={!mayWrite}
            onChange={(e) => set({ admission_source: e.target.value || undefined })}>
            <option value="">— not set —</option>
            {ADMISSION_SOURCES.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
          </NativeSelect>
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold">Department / specialty</label>
          <NativeSelect value={data.specialty_id ?? ''} disabled={!mayWrite}
            onChange={(e) => set({ specialty_id: e.target.value ? Number(e.target.value) : null })}>
            <option value="">— none —</option>
            {specialties.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </NativeSelect>
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold">Consultant in charge</label>
          <NativeSelect data-focus="consultant" value={data.consultant_user_id ?? ''} disabled={!mayWrite}
            onChange={(e) => set({ consultant_user_id: e.target.value ? Number(e.target.value) : null })}>
            <option value="">— none —</option>
            {doctors.map((d) => <option key={d.id} value={d.id}>{d.full_name}</option>)}
          </NativeSelect>
        </div>
        <div className="sm:col-span-2 lg:col-span-4">
          <label className="mb-1 block text-xs font-semibold">Referred by</label>
          <Input defaultValue={data.referred_by ?? ''} disabled={!mayWrite}
            onBlur={(e) => { if (e.target.value !== (data.referred_by ?? '')) set({ referred_by: e.target.value }); }}
            placeholder="Referring doctor / facility" />
        </div>
      </div>
    </Section>
  );
}

// ── Attenders / next of kin ───────────────────────────────────────

function AttendersCard({ admissionId, mayWrite }: { admissionId: number; mayWrite: boolean }) {
  const { data, isLoading } = useAttenders(admissionId);
  const create = useCreateAttender(admissionId);
  const update = useUpdateAttender(admissionId);
  const del = useDeleteAttender(admissionId);
  const { fail, node, clear } = useErr();
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ name: '', relationship: '', mobile: '', is_primary: false, is_legal_representative: false, id_proof_type: '' as IdProofType | '' });

  const set = (k: keyof typeof f, v: string | boolean) => setF((p) => ({ ...p, [k]: v }));
  const reset = () => setF({ name: '', relationship: '', mobile: '', is_primary: false, is_legal_representative: false, id_proof_type: '' });

  return (
    <Section
      focusId="attenders"
      title="Attenders / next of kin"
      action={mayWrite && !adding && <Button size="sm" data-focus="attender:new" onClick={() => setAdding(true)}>+ Attender</Button>}
    >
      {adding && (
        <div className="mb-3 flex flex-wrap items-end gap-2 rounded-lg border border-border bg-muted/40 p-3">
          <div className="min-w-[160px] flex-1">
            <label className="mb-1 block text-xs font-semibold">Name</label>
            <Input value={f.name} onChange={(e) => set('name', e.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold">Relationship</label>
            <Input className="w-32" value={f.relationship} onChange={(e) => set('relationship', e.target.value)} placeholder="Wife / Father" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold">Mobile</label>
            <Input className="w-36" value={f.mobile} onChange={(e) => set('mobile', e.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold">ID proof</label>
            <NativeSelect className="w-28" value={f.id_proof_type} onChange={(e) => set('id_proof_type', e.target.value)}>
              <option value="">— none —</option>
              {ID_PROOF_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </NativeSelect>
          </div>
          <label className="flex items-center gap-1.5 pb-2 text-[12.5px]">
            <input type="checkbox" checked={f.is_primary} onChange={(e) => set('is_primary', e.target.checked)} />
            Primary
          </label>
          <label className="flex items-center gap-1.5 pb-2 text-[12.5px]">
            <input type="checkbox" checked={f.is_legal_representative} onChange={(e) => set('is_legal_representative', e.target.checked)} />
            Legal representative
          </label>
          <div className="flex gap-2 pb-0.5">
            <Button variant="ghost" size="sm" onClick={() => { setAdding(false); clear(); reset(); }}>Cancel</Button>
            <Button size="sm" loading={create.isPending} disabled={!f.name.trim()}
              onClick={() => {
                clear();
                create.mutate({ ...f, id_proof_type: f.id_proof_type || undefined } as never, {
                  onSuccess: () => { setAdding(false); reset(); },
                  onError: fail,
                });
              }}>Add</Button>
          </div>
        </div>
      )}
      {node}

      {isLoading ? <LoadingState label="Loading attenders…" />
        : !data?.length ? <Empty label="No attenders recorded" />
        : (
          <div className="space-y-2">
            {data.map((a) => (
              <div key={a.id} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-lg border border-border px-3 py-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[13px] font-semibold">{a.name}</span>
                  {a.relationship && <span className="text-[12px] text-ink-muted">({a.relationship})</span>}
                  {a.mobile && <span className="text-[12px] text-ink-muted">{a.mobile}</span>}
                  {a.is_primary && <Badge variant="success">Primary</Badge>}
                  {a.is_legal_representative && <Badge variant="info">LAR</Badge>}
                </div>
                {mayWrite && (
                  <div className="flex gap-1.5">
                    {!a.is_primary && (
                      <Button variant="ghost" size="sm"
                        onClick={() => update.mutate({ attenderId: a.id, input: { is_primary: true } }, { onError: fail })}>
                        Make primary
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => del.mutate(a.id, { onError: fail })}>Remove</Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
    </Section>
  );
}

// ── Medico-legal case ──────────────────────────────────────────────

function MlcCard({ admissionId, mayWrite }: { admissionId: number; mayWrite: boolean }) {
  const { data, isLoading } = useMlc(admissionId);
  const save = useSaveMlc(admissionId);
  const { fail, node, clear } = useErr();
  // `is_mlc: null` = nobody has answered yet. No saved row is "not yet
  // answered" (the MRD checklist's mlc_unanswered gap, and the printed
  // packet), which is a different fact from "answered No" — so neither radio
  // may look selected until someone actually picks one. Defaulting to `false`
  // showed "No" ticked on every new admission, so staff skipped a question
  // the checklist kept saying was unanswered.
  const [f, setF] = useState<{ is_mlc: boolean | null; category: string; informant: string; police_station: string; intimation_ref: string }>(
    { is_mlc: null, category: '', informant: '', police_station: '', intimation_ref: '' });

  useEffect(() => {
    if (data) setF({ is_mlc: data.is_mlc, category: data.category ?? '', informant: data.informant ?? '', police_station: data.police_station ?? '', intimation_ref: data.intimation_ref ?? '' });
  }, [data]);

  const set = (k: keyof typeof f, v: string | boolean) => setF((p) => ({ ...p, [k]: v }));
  const unanswered = !data && f.is_mlc === null;
  const commit = (patch: Record<string, unknown>) => { clear(); save.mutate(patch as never, { onError: fail }); };

  if (isLoading) return <Section title="Medico-legal case"><LoadingState label="Loading…" /></Section>;

  return (
    <Section focusId="mlc" title="Medico-legal case (MLC)">
      {node}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1 block text-xs font-semibold">
            Is this an MLC?
            {unanswered && <Badge variant="warn" className="ml-2 align-middle">Not yet answered</Badge>}
          </label>
          <div className="flex gap-3 text-[13px]">
            {(['No', 'Yes'] as const).map((opt) => (
              <label key={opt} className="flex items-center gap-1.5">
                <input type="radio" name={`mlc-${admissionId}`} disabled={!mayWrite}
                  checked={f.is_mlc === (opt === 'Yes')}
                  onChange={() => { set('is_mlc', opt === 'Yes'); commit({ is_mlc: opt === 'Yes' }); }} />
                {opt}
              </label>
            ))}
          </div>
        </div>
        {f.is_mlc && (
          <>
            <div>
              <label className="mb-1 block text-xs font-semibold">Category</label>
              <Input className="w-36" value={f.category} disabled={!mayWrite}
                onChange={(e) => set('category', e.target.value)}
                onBlur={() => commit({ category: f.category })} placeholder="RTA / assault / …" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold">Informant</label>
              <Input className="w-36" value={f.informant} disabled={!mayWrite}
                onChange={(e) => set('informant', e.target.value)}
                onBlur={() => commit({ informant: f.informant })} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold">Police station</label>
              <Input className="w-40" value={f.police_station} disabled={!mayWrite}
                onChange={(e) => set('police_station', e.target.value)}
                onBlur={() => commit({ police_station: f.police_station })} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold">Intimation ref.</label>
              <Input className="w-32" value={f.intimation_ref} disabled={!mayWrite}
                onChange={(e) => set('intimation_ref', e.target.value)}
                onBlur={() => commit({ intimation_ref: f.intimation_ref })} />
            </div>
          </>
        )}
      </div>
      {data?.updated_at && <div className="mt-2 text-[11px] text-ink-muted">Last updated {fmtDateTime(data.updated_at)}</div>}
    </Section>
  );
}

// ── Legal hold (§9.4, Phase 9) ───────────────────────────────────────
//
// Read is open to anyone who can see the Face sheet — a clinician should be
// able to see an admission is under legal hold without the admin:legal-hold
// grant. Setting a manual (litigation) hold and clearing any hold require
// that grant and the Admin role (enforced server-side) — mayWrite here only
// controls whether the controls render at all. An 'mlc'/'death' hold is
// never set from here; it is engaged automatically by the MLC card above
// and by signing a death-type discharge summary.
function LegalHoldCard({ admissionId, mayWrite }: { admissionId: number; mayWrite: boolean }) {
  const { data, isLoading } = useLegalHold(admissionId);
  const setHold = useSetLegalHold(admissionId);
  const clearHold = useClearLegalHold(admissionId);
  const { fail, node, clear } = useErr();
  const [reason, setReason] = useState('');
  const [clearing, setClearing] = useState(false);

  if (isLoading || !data) return null;
  if (!data.legal_hold && !mayWrite) return null; // nothing to show a non-MRD viewer when there's no hold

  const sourceLabel: Record<string, string> = { mlc: 'Medico-legal case', death: 'Death during admission', litigation: 'Litigation', manual: 'Manual' };

  return (
    <Section title="Legal hold" subtitle="Blocks a DPDP erasure request for this patient until cleared or overridden.">
      {node}
      {data.legal_hold ? (
        <div className="flex flex-wrap items-center gap-3">
          <Badge variant="danger">⚠ Under legal hold</Badge>
          <span className="text-[13px]">
            {sourceLabel[data.legal_hold_source ?? ''] ?? data.legal_hold_source} — {data.legal_hold_reason}
            {data.legal_hold_set_at && <> (set {fmtDateTime(data.legal_hold_set_at)}{data.legal_hold_set_by ? ` by ${data.legal_hold_set_by}` : ''})</>}
          </span>
          {mayWrite && (
            <Button size="sm" variant="ghost" onClick={() => { clear(); clearHold.reset(); setClearing(true); }}>
              Clear hold
            </Button>
          )}
        </div>
      ) : mayWrite ? (
        <div className="flex flex-wrap items-end gap-2">
          <Input className="w-64" placeholder="Reason for a litigation hold…" value={reason}
            onChange={(e) => setReason(e.target.value)} />
          <Button size="sm" variant="ghost" disabled={!reason.trim() || setHold.isPending}
            onClick={() => { clear(); setHold.mutate(reason.trim(), { onSuccess: () => setReason(''), onError: fail }); }}>
            Set litigation hold
          </Button>
        </div>
      ) : null}
      <TextPromptDialog
        open={clearing}
        title="Clear the legal hold?"
        description="A DPDP erasure request for this patient will no longer be blocked by this admission."
        label="Reason for clearing this legal hold"
        confirmLabel="Clear hold"
        danger
        pending={clearHold.isPending}
        error={clearHold.error instanceof Error ? clearHold.error.message : null}
        onConfirm={(why) => clearHold.mutate(why, { onSuccess: () => setClearing(false) })}
        onClose={() => setClearing(false)}
      />
    </Section>
  );
}

// ── Payer / scheme ──────────────────────────────────────────────────

// A bare "insurance"/"pmjay"/etc. scheme category says "not self-pay" but
// not whom to bill — utils/claimAutoCreate.js (backend) only opens a claim
// once an insurer/TPA ORGANISATION is picked here too. Self-pay never shows
// this picker at all.
const SCHEMES_WITH_PAYER_ORG = new Set(['insurance', 'pmjay', 'cmchis', 'cghs', 'esi', 'corporate']);
const ID_PROOF_OPTIONS: Array<{ value: IdProofType; label: string }> = [
  { value: 'aadhaar', label: 'Aadhaar' }, { value: 'voter', label: 'Voter ID' },
  { value: 'dl', label: 'Driving licence' }, { value: 'passport', label: 'Passport' }, { value: 'other', label: 'Other' },
];

function PayerCard({ admissionId, mayWrite }: { admissionId: number; mayWrite: boolean }) {
  const { data: schemes } = usePayerSchemes();
  const { data: payerOrgs } = usePayersMaster();
  const { data, isLoading } = usePayers(admissionId);
  const create = useCreatePayer(admissionId);
  const update = useUpdatePayer(admissionId);
  const verify = useVerifyPayerId(admissionId);
  const del = useDeletePayer(admissionId);
  const { fail, node, clear } = useErr();
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({
    scheme_code: '', card_no: '', preauth_no: '', is_primary: false,
    insurer_payer_id: '', id_proof_type: '' as IdProofType | '', id_proof_last4: '',
  });

  return (
    <Section
      title="Payer / scheme"
      subtitle="Hidden from the Patient copy when the case sheet is generated. Picking an insurer/TPA opens a claim automatically."
      action={mayWrite && !adding && <Button size="sm" onClick={() => setAdding(true)}>+ Payer</Button>}
    >
      {adding && (
        <div className="mb-3 flex flex-wrap items-end gap-2 rounded-lg border border-border bg-muted/40 p-3">
          <div>
            <label className="mb-1 block text-xs font-semibold">Scheme</label>
            <NativeSelect className="w-44" value={f.scheme_code} onChange={(e) => setF((p) => ({ ...p, scheme_code: e.target.value }))}>
              <option value="">— select —</option>
              {(schemes ?? []).map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
            </NativeSelect>
          </div>
          {SCHEMES_WITH_PAYER_ORG.has(f.scheme_code) && (
            <div>
              <label className="mb-1 block text-xs font-semibold">Insurer / TPA</label>
              <NativeSelect className="w-48" value={f.insurer_payer_id} onChange={(e) => setF((p) => ({ ...p, insurer_payer_id: e.target.value }))}>
                <option value="">— not picked yet —</option>
                {(payerOrgs ?? []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </NativeSelect>
            </div>
          )}
          <div>
            <label className="mb-1 block text-xs font-semibold">Card / member no.</label>
            <Input className="w-36" value={f.card_no} onChange={(e) => setF((p) => ({ ...p, card_no: e.target.value }))} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold">Pre-auth no.</label>
            <Input className="w-36" value={f.preauth_no} onChange={(e) => setF((p) => ({ ...p, preauth_no: e.target.value }))} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold">ID proof</label>
            <NativeSelect className="w-32" value={f.id_proof_type} onChange={(e) => setF((p) => ({ ...p, id_proof_type: e.target.value as IdProofType }))}>
              <option value="">— none —</option>
              {ID_PROOF_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </NativeSelect>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold">Last 4 chars</label>
            <Input className="w-20" maxLength={4} value={f.id_proof_last4}
              placeholder="e.g. 1234" onChange={(e) => setF((p) => ({ ...p, id_proof_last4: e.target.value }))} />
          </div>
          <label className="flex items-center gap-1.5 pb-2 text-[12.5px]">
            <input type="checkbox" checked={f.is_primary} onChange={(e) => setF((p) => ({ ...p, is_primary: e.target.checked }))} />
            Primary
          </label>
          <div className="flex gap-2 pb-0.5">
            <Button variant="ghost" size="sm" onClick={() => { setAdding(false); clear(); }}>Cancel</Button>
            <Button size="sm" loading={create.isPending} disabled={!f.scheme_code}
              onClick={() => {
                clear();
                create.mutate({
                  scheme_code: f.scheme_code, card_no: f.card_no || undefined, preauth_no: f.preauth_no || undefined,
                  is_primary: f.is_primary, insurer_payer_id: f.insurer_payer_id ? Number(f.insurer_payer_id) : undefined,
                  id_proof_type: f.id_proof_type || undefined, id_proof_last4: f.id_proof_last4 || undefined,
                }, {
                  onSuccess: () => {
                    setAdding(false);
                    setF({ scheme_code: '', card_no: '', preauth_no: '', is_primary: false, insurer_payer_id: '', id_proof_type: '', id_proof_last4: '' });
                  },
                  onError: fail,
                });
              }}>Add</Button>
          </div>
        </div>
      )}
      {node}

      {isLoading ? <LoadingState label="Loading payers…" />
        : !data?.length ? <Empty label="No payer recorded (self-pay by default)" />
        : (
          <div className="space-y-2">
            {data.map((p) => {
              const orgName = (payerOrgs ?? []).find((o) => o.id === p.insurer_payer_id)?.name;
              return (
                <div key={p.id} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-lg border border-border px-3 py-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[13px] font-semibold">
                      {(schemes ?? []).find((s) => s.code === p.scheme_code)?.label ?? p.scheme_code}
                    </span>
                    {orgName && <span className="text-[12px] text-ink-muted">{orgName}</span>}
                    {p.card_no && <span className="text-[12px] text-ink-muted">Card {p.card_no}</span>}
                    {p.preauth_no && <span className="text-[12px] text-ink-muted">Pre-auth {p.preauth_no}{p.preauth_status ? ` (${p.preauth_status})` : ''}</span>}
                    {p.id_proof_type && (
                      <span className="text-[12px] text-ink-muted">
                        ID {p.id_proof_type} ••{p.id_proof_last4}
                        {p.id_verified_at ? ' ✓ verified' : ''}
                      </span>
                    )}
                    {p.is_primary && <Badge variant="success">Primary</Badge>}
                    {orgName && <Badge variant={p.id_verified_at ? 'success' : 'warn'}>{p.id_verified_at ? 'Claim open' : 'Claim pending ID verify'}</Badge>}
                  </div>
                  <div className="flex gap-1">
                    {mayWrite && p.id_proof_type && p.id_proof_last4 && !p.id_verified_at && (
                      <Button variant="ghost" size="sm" onClick={() => verify.mutate(p.id, { onError: fail })}>Verify ID</Button>
                    )}
                    {mayWrite && SCHEMES_WITH_PAYER_ORG.has(p.scheme_code) && !p.insurer_payer_id && (
                      <NativeSelect className="w-40 text-[12px]" defaultValue=""
                        onChange={(e) => e.target.value && update.mutate({ payerId: p.id, input: { insurer_payer_id: Number(e.target.value) } }, { onError: fail })}>
                        <option value="">Pick insurer/TPA…</option>
                        {(payerOrgs ?? []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                      </NativeSelect>
                    )}
                    {mayWrite && <Button variant="ghost" size="sm" onClick={() => del.mutate(p.id, { onError: fail })}>Remove</Button>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
    </Section>
  );
}
